import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Monitor,
  TrendingUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { fadeIn, fadeInUp, stagger } from "../../lib/motion";
import { useAppStore } from "../../store/useAppStore";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { PageHeader } from "../ui/page-header";

interface MonitoringMetrics {
  totalDeployments: number;
  runningDeployments: number;
  failedDeployments: number;
  averageResponseTime: string;
  errorRate: string;
  uptime: string;
}

export function MonitoringView() {
  const { deployments, projects } = useAppStore();
  const [metrics, setMetrics] = useState<MonitoringMetrics>({
    totalDeployments: 0,
    runningDeployments: 0,
    failedDeployments: 0,
    averageResponseTime: "0ms",
    errorRate: "0%",
    uptime: "100%",
  });

  useEffect(() => {
    const runningCount = deployments.filter(
      (d) => d.status === "running"
    ).length;
    const failedCount = deployments.filter((d) => d.status === "failed").length;

    setMetrics({
      totalDeployments: deployments.length,
      runningDeployments: runningCount,
      failedDeployments: failedCount,
      averageResponseTime: runningCount > 0 ? "245ms" : "0ms",
      errorRate:
        deployments.length > 0
          ? `${((failedCount / deployments.length) * 100).toFixed(2)}%`
          : "0%",
      uptime:
        deployments.length > 0
          ? `${(
              ((deployments.length - failedCount) / deployments.length) *
              100
            ).toFixed(2)}%`
          : "100%",
    });
  }, [deployments]);

  return (
    <div className="space-y-6">
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <PageHeader
          eyebrow="Observability"
          title="Monitoring"
          description="Monitor your application health and performance metrics"
        />
      </motion.div>

      {deployments.length === 0 ? (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <Monitor className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-h4">
                  No deployments to monitor
                </h3>
                <p className="text-muted-foreground">
                  Deploy your first application to start monitoring metrics
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          {/* KPI cards */}
          <motion.div
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    System Status
                  </CardTitle>
                  {metrics.runningDeployments > 0 ? (
                    <CheckCircle className="h-4 w-4 text-success" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  )}
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-h2 ${
                      metrics.runningDeployments > 0
                        ? "text-success"
                        : "text-warning"
                    }`}
                  >
                    {metrics.runningDeployments > 0
                      ? "Healthy"
                      : "No Active Services"}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {metrics.runningDeployments} of {metrics.totalDeployments}{" "}
                    deployments running
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Response Time
                  </CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-h2">
                    {metrics.averageResponseTime}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Average response time
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Error Rate
                  </CardTitle>
                  <AlertTriangle
                    className={`h-4 w-4 ${
                      metrics.failedDeployments > 0
                        ? "text-destructive"
                        : "text-success"
                    }`}
                  />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-h2 ${
                      metrics.failedDeployments > 0
                        ? "text-destructive"
                        : "text-success"
                    }`}
                  >
                    {metrics.errorRate}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {metrics.failedDeployments} failed deployments
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Success Rate
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-h2">{metrics.uptime}</div>
                  <p className="text-xs text-muted-foreground">
                    Deployment success rate
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>

          {/* Detail cards */}
          <motion.div
            className="grid gap-6 md:grid-cols-2"
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader>
                  <CardTitle>Recent Deployments</CardTitle>
                </CardHeader>
                <CardContent>
                  <motion.div
                    className="space-y-3"
                    variants={stagger}
                    initial="hidden"
                    animate="visible"
                  >
                    {deployments.slice(0, 5).map((deployment) => (
                      <motion.div
                        key={deployment.id}
                        variants={fadeInUp}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`h-2 w-2 rounded-full ${
                              deployment.status === "running"
                                ? "bg-success"
                                : deployment.status === "failed"
                                  ? "bg-destructive"
                                  : "bg-warning"
                            }`}
                          />
                          <span className="text-sm font-medium">
                            {deployment.name}
                          </span>
                        </div>
                        <span className="text-sm capitalize text-muted-foreground">
                          {deployment.status}
                        </span>
                      </motion.div>
                    ))}
                    {deployments.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No deployments yet
                      </p>
                    )}
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Card>
                <CardHeader>
                  <CardTitle>Project Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <motion.div
                    className="space-y-3"
                    variants={stagger}
                    initial="hidden"
                    animate="visible"
                  >
                    {projects.slice(0, 5).map((project) => {
                      const projectDeployments = deployments.filter(
                        (d) => d.projectId === project.id
                      );
                      const hasRunningDeployment = projectDeployments.some(
                        (d) => d.status === "running"
                      );

                      return (
                        <motion.div
                          key={project.id}
                          variants={fadeInUp}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className={`h-2 w-2 rounded-full ${
                                hasRunningDeployment
                                  ? "bg-success"
                                  : "bg-muted-foreground"
                              }`}
                            />
                            <span className="text-sm font-medium">
                              {project.name}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {projectDeployments.length} deployment
                            {projectDeployments.length !== 1 ? "s" : ""}
                          </span>
                        </motion.div>
                      );
                    })}
                    {projects.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No projects yet
                      </p>
                    )}
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </>
      )}
    </div>
  );
}
